import React from 'react';
import { Alert, Table, Stack, Text, Center, Loader } from "@mantine/core";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

interface AdminTableProps {
  /** Table headers */
  headers: string[];
  /** Table data rows */
  children: React.ReactNode;
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string | null;
  /** Empty state message */
  emptyMessage?: string;
  /** Whether data array is empty */
  isEmpty?: boolean;
  /** Additional table classes */
  className?: string;
  /** Whether to show striped rows */
  striped?: boolean;
  /** Whether to show hover effect */
  hover?: boolean;
  /** Table size */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * Standardized table component for admin pages
 * Handles loading, error, and empty states consistently
 */
const AdminTable: React.FunctionComponent<AdminTableProps> = ({
  headers,
  children,
  loading = false,
  error = null,
  emptyMessage = "No data available",
  isEmpty = false,
  className = "",
  striped = true,
  hover = true,
  size = 'sm'
}) => {
  // Loading state
  if (loading) {
    return (
      <Center py="xl">
        <Stack align="center" gap="md">
          <Loader color="brand" size="lg" />
          <Text c="dimmed" size="sm">Loading data...</Text>
        </Stack>
      </Center>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert color="red" variant="light">
        <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '8px' }} />
        <strong>Error:</strong> {error}
      </Alert>
    );
  }

  // Empty state
  if (isEmpty) {
    return (
      <Alert color="blue" variant="light">
        <Center>
          <Stack align="center" gap="xs">
            <FontAwesomeIcon icon={faInfoCircle} />
            <Text size="sm">{emptyMessage}</Text>
          </Stack>
        </Center>
      </Alert>
    );
  }

  // Table content
  return (
    <Table.ScrollContainer minWidth={500}>
      <Table
        striped={striped}
        highlightOnHover={hover}
        className={className}
        verticalSpacing="sm"
        styles={{
          table: {
            backgroundColor: 'var(--color-admin-card)',
          },
          th: {
            backgroundColor: 'var(--color-table-header)',
            fontWeight: 600,
            fontSize: '13px',
            color: 'var(--color-text-primary)',
            padding: 'var(--spacing-table-cell)',
            borderBottom: '2px solid var(--color-table-border)'
          },
          td: {
            padding: 'var(--spacing-table-cell)',
            borderBottom: '1px solid var(--color-table-border)',
            color: 'var(--color-text-primary)',
          },
          tr: {
            '&:hover': {
              backgroundColor: 'var(--color-table-row-hover) !important',
            },
            '&:nth-of-type(even)': {
              backgroundColor: 'var(--color-table-stripe)',
            }
          }
        }}
      >
        <Table.Thead>
          <Table.Tr>
            {headers.map((header, index) => (
              <Table.Th key={index}>
                {header}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {children}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
};

export default AdminTable;
